import httpClient from "./httpClient";
import { generateIdempotencyKey, getPage } from "./apiClient";

let tarefaCheckConfigCache = null;
let tarefaCheckConfigPromise = null;

function serializeListParam(values = []) {
  if (!Array.isArray(values) || values.length === 0) {
    return undefined;
  }

  const normalized = values
    .map((value) => Number(value))
    .filter(Number.isFinite);

  return normalized.length > 0 ? normalized.join(",") : undefined;
}

function normalizeTask(task = {}) {
  const normalized = { ...task, id: task.id ?? task.idTarefa ?? null };
  if (!normalized.usuario && normalized.idUsuario) {
    normalized.usuario = { id: normalized.idUsuario };
  }
  if (!normalized.statusAtividade && normalized.idStatusAtividade) {
    normalized.statusAtividade = { id: normalized.idStatusAtividade };
  }
  if (!normalized.tipoDeTarefa && normalized.idTipoDeTarefa) {
    normalized.tipoDeTarefa = { id: normalized.idTipoDeTarefa };
  }
  return normalized;
}

function normalizeTaskPayload(tarefa = {}) {
  const parceirosIdsSource = tarefa.parceirosIds
    ?? tarefa.parceiroIds
    ?? (Array.isArray(tarefa.parceiros) ? tarefa.parceiros.map((p) => p?.id).filter(Boolean) : undefined);
  const parceirosIds = Array.isArray(parceirosIdsSource)
    ? parceirosIdsSource.map((id) => Number(id)).filter(Number.isFinite)
    : undefined;
  const motivosIds = tarefa.motivosIds
    || tarefa.motivoIds
    || (Array.isArray(tarefa.motivos) ? tarefa.motivos.map((m) => m?.id).filter(Boolean) : undefined);

  return {
    descricaoTarefa: tarefa.descricaoTarefa ?? tarefa.descricao ?? "",
    dataCriacao: tarefa.dataCriacao ?? null,
    dataDeVencimento: tarefa.dataDeVencimento ?? null,
    dataInicio: tarefa.dataInicio ?? null,
    dataFim: tarefa.dataFim ?? null,
    dataConclusao: tarefa.dataConclusao ?? null,
    prioridade: tarefa.prioridade ?? null,
    motivosIds: motivosIds ?? null,
    observacoes: tarefa.observacoes ?? "",
    relatorio: tarefa.relatorio ?? null,
    dadosFinanceiros: tarefa.dadosFinanceiros ?? null,
    produto: tarefa.produto ?? null,
    tipoProduto: tarefa.tipoProduto ?? null,
    comissaoParceiroAjustada: tarefa.comissaoParceiroAjustada ?? null,
    loginParceiroSolicitado: tarefa.loginParceiroSolicitado ?? null,
    idStatusAtividade: tarefa.idStatusAtividade ?? tarefa.statusId ?? tarefa.statusAtividade?.id ?? null,
    idTipoDeTarefa: tarefa.idTipoDeTarefa ?? tarefa.tipoId ?? tarefa.tipoDeTarefa?.id ?? null,
    idUsuario: tarefa.idUsuario ?? tarefa.usuarioId ?? tarefa.usuario?.id ?? null,
    parceirosIds: parceirosIds ?? null,
  };
}

function normalizeFiles(files) {
  if (!files) return [];
  if (Array.isArray(files)) return files.filter(Boolean);
  if (typeof FileList !== "undefined" && files instanceof FileList) return Array.from(files).filter(Boolean);
  return [];
}

export async function listarTarefasPaginado({
  page = 0,
  size = 20,
  q,
  parceiroId,
  parceiroIds = [],
  statusId,
  statusIds = [],
  usuarioId,
  usuarioIds = [],
  tipoIds = [],
  dataInicio,
  dataFim,
  incluirInativas = false,
} = {}) {
  const parceiroIdsNormalizados = Array.isArray(parceiroIds) && parceiroIds.length > 0
    ? parceiroIds
    : (parceiroId != null ? [parceiroId] : []);
  const statusIdsNormalizados = Array.isArray(statusIds) && statusIds.length > 0
    ? statusIds
    : (statusId != null ? [statusId] : []);
  const usuarioIdsNormalizados = Array.isArray(usuarioIds) && usuarioIds.length > 0
    ? usuarioIds
    : (usuarioId != null ? [usuarioId] : []);

  const response = await getPage("/api/tarefas", {
    page,
    size,
    q: q || undefined,
    parceiroIds: serializeListParam(parceiroIdsNormalizados),
    statusIds: serializeListParam(statusIdsNormalizados),
    usuarioIds: serializeListParam(usuarioIdsNormalizados),
    tipoIds: serializeListParam(tipoIds),
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
    incluirInativas: incluirInativas ? true : undefined,
  });
  return {
    ...response,
    content: response.content.map(normalizeTask),
  };
}

export async function listarTarefas() {
  const page = await listarTarefasPaginado();
  return page.content;
}

export async function listarTarefasPorParceiro(parceiroId) {
  const page = await listarTarefasPaginado({ parceiroId });
  return page.content;
}

export async function buscarTarefa(id) {
  try {
    const resp = await httpClient.get(`/api/tarefas/${id}`);
    return normalizeTask(resp.data);
  } catch {
    return null;
  }
}

export async function listarHistoricoTarefa(id) {
  const resp = await httpClient.get(`/api/tarefas/${id}/historico`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function obterConfiguracaoCheckTarefa({ forceRefresh = false } = {}) {
  if (!forceRefresh && tarefaCheckConfigCache) {
    return tarefaCheckConfigCache;
  }

  if (!forceRefresh && tarefaCheckConfigPromise) {
    return tarefaCheckConfigPromise;
  }

  tarefaCheckConfigPromise = httpClient
    .get("/api/tarefas/check-config")
    .then((resp) => {
      const radiusMeters = Number(resp.data?.radiusMeters);
      tarefaCheckConfigCache = {
        radiusMeters: Number.isFinite(radiusMeters) && radiusMeters > 0 ? radiusMeters : 200,
      };
      return tarefaCheckConfigCache;
    })
    .finally(() => {
      tarefaCheckConfigPromise = null;
    });

  return tarefaCheckConfigPromise;
}

export async function criarTarefa(tarefa) {
  const payload = normalizeTaskPayload(tarefa);
  const resp = await httpClient.post("/api/tarefas", payload, {
    headers: { "X-Idempotency-Key": generateIdempotencyKey("task-create") },
  });
  return normalizeTask(resp.data);
}

export async function atualizarTarefa(id, tarefa) {
  const payload = normalizeTaskPayload(tarefa);
  const resp = await httpClient.put(`/api/tarefas/${id}`, payload, {
    headers: { "X-Idempotency-Key": generateIdempotencyKey(`task-update-${id}`) },
  });
  return normalizeTask(resp.data);
}

export async function removerTarefa(id) {
  await httpClient.delete(`/api/tarefas/${id}`);
}

export async function countTarefasPorUsuarioTipoStatus(userId, tipoId, statusId) {
  const resp = await httpClient.get("/api/tarefas/count", {
    params: { userId, tipoId, statusId },
  });
  return resp.data;
}

export async function countTarefasPorUsuarioStatus(userId, statusId) {
  const resp = await httpClient.get("/api/tarefas/count/status", {
    params: { userId, statusId },
  });
  return resp.data;
}

export async function checkinTarefa(tarefaId, geoPayload) {
  const resp = await httpClient.post(`/api/tarefas/${tarefaId}/checkin`, geoPayload, {
    headers: { "X-Idempotency-Key": generateIdempotencyKey(`checkin-${tarefaId}`) },
  });
  return resp.data;
}

export async function checkoutTarefa(tarefaId, geoPayload) {
  const resp = await httpClient.post(`/api/tarefas/${tarefaId}/checkout`, geoPayload, {
    headers: { "X-Idempotency-Key": generateIdempotencyKey(`checkout-${tarefaId}`) },
  });
  return resp.data;
}

export async function fallbackCheckinTarefa(tarefaId, payload) {
  const files = normalizeFiles(payload?.files);
  const requestKey = generateIdempotencyKey(`fallback-checkin-${tarefaId}`);
  if (files.length > 0) {
    const formData = new FormData();
    formData.append("motivo", payload?.motivo || "");
    files.forEach((file) => formData.append("fotos", file));
    const resp = await httpClient.post(`/api/tarefas/${tarefaId}/checkin/fallback/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Idempotency-Key": requestKey,
      },
    });
    return resp.data;
  }
  const resp = await httpClient.post(`/api/tarefas/${tarefaId}/checkin/fallback`, payload || {}, {
    headers: { "X-Idempotency-Key": requestKey },
  });
  return resp.data;
}

export async function fallbackCheckoutTarefa(tarefaId, payload) {
  const files = normalizeFiles(payload?.files);
  const requestKey = generateIdempotencyKey(`fallback-checkout-${tarefaId}`);
  if (files.length > 0) {
    const formData = new FormData();
    formData.append("motivo", payload?.motivo || "");
    files.forEach((file) => formData.append("fotos", file));
    const resp = await httpClient.post(`/api/tarefas/${tarefaId}/checkout/fallback/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Idempotency-Key": requestKey,
      },
    });
    return resp.data;
  }
  const resp = await httpClient.post(`/api/tarefas/${tarefaId}/checkout/fallback`, payload || {}, {
    headers: { "X-Idempotency-Key": requestKey },
  });
  return resp.data;
}

export async function evidenciarTarefa(tarefaId, payload) {
  const files = normalizeFiles(payload?.files);
  const requestKey = generateIdempotencyKey(`evidencia-${tarefaId}`);
  if (files.length > 0) {
    const formData = new FormData();
    if (payload?.observacao != null) {
      formData.append("observacao", payload.observacao);
    }
    files.forEach((file) => formData.append("fotos", file));
    const resp = await httpClient.post(`/api/tarefas/${tarefaId}/evidencia/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Idempotency-Key": requestKey,
      },
    });
    return resp.data;
  }
  const resp = await httpClient.post(`/api/tarefas/${tarefaId}/evidencia`, payload || {}, {
    headers: { "X-Idempotency-Key": requestKey },
  });
  return resp.data;
}

export async function concluirTarefa(tarefaId) {
  return concluirTarefaComPayload(tarefaId, {});
}

export async function concluirTarefaComPayload(tarefaId, payload) {
  const resp = await httpClient.post(
    `/api/tarefas/${tarefaId}/concluir`,
    payload || {},
    { headers: { "X-Idempotency-Key": generateIdempotencyKey(`conclude-${tarefaId}`) } }
  );
  return resp.data;
}

export async function listarChecksTarefa(tarefaId) {
  const resp = await httpClient.get(`/api/tarefas/${tarefaId}/checks`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function listarPendenciasAprovacao(tipo) {
  const resp = await httpClient.get("/api/tarefas/pendencias", {
    params: { tipo: tipo || undefined },
  });
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function aprovarCheckTarefa(checkId, obs) {
  const resp = await httpClient.post(`/api/tarefas/check/${checkId}/aprovar`, { obs: obs || "" });
  return resp.data;
}

export async function reprovarCheckTarefa(checkId, obs) {
  const resp = await httpClient.post(`/api/tarefas/check/${checkId}/reprovar`, { obs: obs || "" });
  return resp.data;
}
